import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Upload, Sliders, Users, LogOut, Leaf } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, roles: ["admin", "analyst", "viewer"] },
  { to: "/upload", label: "Upload", icon: Upload, roles: ["admin"] },
  { to: "/metric-studio", label: "Metric Studio", icon: Sliders, roles: ["admin", "analyst"] },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-app text-stone-800 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-stone-200 bg-white shrink-0" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-brand text-white grid place-items-center">
              <Leaf className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading font-medium text-[15px] tracking-tight text-stone-900 leading-tight">
                Aromathai
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                MIS Dashboard
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-brand text-white"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  )
                }
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-stone-200 space-y-2">
          <div className="text-xs">
            <div className="font-medium text-stone-800 truncate" data-testid="user-name">{user?.name || user?.email}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-brand mt-0.5" data-testid="user-role">
              {user?.role}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <div className="md:hidden fixed top-0 inset-x-0 h-14 border-b border-stone-200 bg-white z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-brand text-white grid place-items-center">
            <Leaf className="w-3.5 h-3.5" />
          </div>
          <div className="font-heading font-medium text-sm tracking-tight">Aromathai</div>
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => cn(
              "px-2.5 py-1 rounded text-xs whitespace-nowrap",
              isActive ? "bg-brand text-white" : "text-stone-600"
            )}>
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 md:pt-0 pt-14 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
